import { useState } from 'react';
import { api } from '../../lib/api';
import type { PlatformSettings } from '../../lib/types';
import { useParent } from '../ParentContext';

/**
 * Agreeing to the Terms (#589): parents tick two boxes at sign-up (the Terms of Service and Privacy Policy, and that
 * they're the parent or guardian). Here: the current version, how many parents accepted it, publishing a new version,
 * and whether existing parents must accept it at their next sign-in.
 */
export function TermsSettings({ saved, onSaved }: { saved: PlatformSettings; onSaved: (s: PlatformSettings) => void }) {
  const { toast } = useParent();
  const [busy, setBusy] = useState(false);
  const l = saved.legal ?? { version: 1, updatedAt: 0, reaccept: true, accepted: 0, parents: 0 };

  async function patch(legal: { reaccept?: boolean; newVersion?: boolean }, done: string) {
    setBusy(true);
    try {
      onSaved(await api.put<PlatformSettings>('/api/admin/settings', { legal }));
      toast({ text: done, tone: 'good' });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack">
      <p className="muted small">
        Parents tick two boxes when they create an account: they agree to the{' '}
        <a href="/terms" target="_blank" rel="noreferrer">
          Terms of Service
        </a>{' '}
        and{' '}
        <a href="/privacy" target="_blank" rel="noreferrer">
          Privacy Policy
        </a>
        , and confirm they’re the parent or legal guardian of any child they add. Each acceptance is saved with the version, date and IP address.
      </p>
      <div className="row-gap terms-admin">
        <div>
          <span className="muted small block">Current version</span>
          <strong className="terms-admin-version">{l.version}</strong>
        </div>
        <div className="grow">
          <span className="muted small block">Accepted the current version</span>
          <strong>
            {l.accepted} of {l.parents} parents
          </strong>
        </div>
        <button
          className="btn"
          disabled={busy}
          onClick={() =>
            confirm(
              `Publish version ${l.version + 1} of the Terms?${l.reaccept ? '\n\nEvery parent will be asked to accept it at their next sign-in.' : ''}`,
            ) && patch({ newVersion: true }, `Version ${l.version + 1} published.`)
          }
        >
          Publish a new version
        </button>
      </div>
      <span className="muted small">
        Publish a new version after you change the Terms or Privacy Policy in a way parents should agree to again. Fixing a typo doesn’t need one.
      </span>
      <label className="check">
        <input type="checkbox" checked={l.reaccept} disabled={busy} onChange={(e) => patch({ reaccept: e.target.checked }, 'Saved.')} />
        <span>
          <strong>Ask existing parents to accept</strong>
          <span className="muted small block">
            Parents who haven’t accepted the current version see “We’ve updated our Terms” at their next sign-in and accept before carrying on. Off: only new parents are
            asked.
          </span>
        </span>
      </label>
    </div>
  );
}
